import { IonButton, IonContent, IonHeader, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import { useStatus } from 'hooks/useStatus';
import { Status } from 'interfaces/Status';
import React from 'react';
import { createUseStyles } from 'react-jss';

import EmptyList from './components/EmptyList';
import ListHeader from './components/ListHeader';
import PerformanceMetrics from './components/PerformanceMetrics';
import PieChartWidget from './components/PieChartWidget';
import ResidentCard from './components/ResidentCard';
import ResponseTimes from './components/ResponseTimes';
import WorkOrderCard from './components/WorkOrderCard';
import usePerformanceMetrics from './usePerformanceMetrics';

const useStyles = createUseStyles({
  content: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  module: {
    minWidth: '335px',
    flex: '1 1 25%',
  },
  widget: {
    padding: '20px 20px 0 20px',
  },
  listItem: {
    animation: '$fadeIn 0.5s ease-in-out',
  },
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(-10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
});

const PerformanceDashboard: React.FC = () => {
  const classes = useStyles();

  const { color: onHoldColor } = useStatus(Status.OnHold);

  const {
    performanceMetrics,
    responseTimes,
    workOrdersByStatus,
    workOrdersByCategory,
    mostEngagedResidents,
    unassignedWorkOrders,
    workOrdersOnHold,
    outstandingWorkOrders,
  } = usePerformanceMetrics();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Performance Dashboard</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.content}>
        <div className={classes.grid}>
          {/* Work orders at a glance */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <PerformanceMetrics
                data={performanceMetrics.data}
                isLoading={performanceMetrics.isLoading}
              />
            </div>
          </div>

          {/* Average response time */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <ResponseTimes data={responseTimes.data} isLoading={responseTimes.isLoading} />
            </div>
          </div>

          {/* Work orders by status */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <PieChartWidget
                title="Work orders by status"
                subtitle="Active work orders"
                data={workOrdersByStatus.data}
                isLoading={workOrdersByStatus.isLoading}
              />
            </div>
          </div>

          {/* Work orders by category */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <PieChartWidget
                title="Work orders by category"
                subtitle={`${new Date().toLocaleString('en-US', { month: 'long' })} work orders`}
                data={workOrdersByCategory.data}
                isLoading={workOrdersByCategory.isLoading}
              />
            </div>
          </div>

          {/* Most engaged residents */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <ListHeader
                title="Most engaged residents"
                subtitle="Top issue reporters this month"
                color="var(--ion-color-primary)"
              />

              {!mostEngagedResidents.isLoading && mostEngagedResidents.visibleItems.length === 0 ? (
                <div className={classes.listItem}>
                  <EmptyList
                    title="No resident reporters"
                    subtitle="No residents have reported issues this month"
                  />
                </div>
              ) : (
                <>
                  {mostEngagedResidents.visibleItems.map((props) => (
                    <div key={props.id} className={classes.listItem}>
                      <ResidentCard key={props.id} {...props} />
                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {mostEngagedResidents.hasMore ? (
                      <IonButton mode="ios" fill="clear" onClick={mostEngagedResidents.showMore}>
                        Show more
                      </IonButton>
                    ) : (
                      mostEngagedResidents.visibleItems.length > 5 && (
                        <IonButton mode="ios" fill="clear" onClick={mostEngagedResidents.showLess}>
                          Show less
                        </IonButton>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Unassigned work orders */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <ListHeader
                title="Unassigned work orders"
                subtitle="Needs assignment to more forward"
              />

              {!unassignedWorkOrders.isLoading && unassignedWorkOrders.visibleItems.length === 0 ? (
                <div className={classes.listItem}>
                  <EmptyList
                    title="No unassigned work orders"
                    subtitle="All work orders have been assigned to team members"
                  />
                </div>
              ) : (
                <>
                  {unassignedWorkOrders.visibleItems.map((props) => (
                    <div key={props.id} className={classes.listItem}>
                      <WorkOrderCard {...props} />
                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {unassignedWorkOrders.hasMore ? (
                      <IonButton mode="ios" fill="clear" onClick={unassignedWorkOrders.showMore}>
                        Show more
                      </IonButton>
                    ) : (
                      unassignedWorkOrders.visibleItems.length > 5 && (
                        <IonButton mode="ios" fill="clear" onClick={unassignedWorkOrders.showLess}>
                          Show less
                        </IonButton>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Work orders on hold */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <ListHeader
                title="Work orders on hold"
                subtitle="On hold pending a blocking issue"
                color={onHoldColor}
              />

              {!workOrdersOnHold.isLoading && workOrdersOnHold.visibleItems.length === 0 ? (
                <div className={classes.listItem}>
                  <EmptyList
                    title="No work orders on hold"
                    subtitle="All work orders are progressing smoothly"
                  />
                </div>
              ) : (
                <>
                  {workOrdersOnHold.visibleItems.map((props) => (
                    <div key={props.id} className={classes.listItem}>
                      <WorkOrderCard {...props} />
                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {workOrdersOnHold.hasMore ? (
                      <IonButton mode="ios" fill="clear" onClick={workOrdersOnHold.showMore}>
                        Show more
                      </IonButton>
                    ) : (
                      workOrdersOnHold.visibleItems.length > 5 && (
                        <IonButton mode="ios" fill="clear" onClick={workOrdersOnHold.showLess}>
                          Show less
                        </IonButton>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Outstanding work orders */}
          <div className={classes.module}>
            <div className={classes.widget}>
              <ListHeader
                title="Outstanding work orders"
                subtitle="Older than the average work order"
                color="#C46A54"
              />

              {!outstandingWorkOrders.isLoading &&
              outstandingWorkOrders.visibleItems.length === 0 ? (
                <div className={classes.listItem}>
                  <EmptyList
                    title="No outstanding work orders"
                    subtitle="All work orders are being completed on time"
                  />
                </div>
              ) : (
                <>
                  {outstandingWorkOrders.visibleItems.map((props) => (
                    <div key={props.id} className={classes.listItem}>
                      <WorkOrderCard {...props} />
                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {outstandingWorkOrders.hasMore ? (
                      <IonButton mode="ios" fill="clear" onClick={outstandingWorkOrders.showMore}>
                        Show more
                      </IonButton>
                    ) : (
                      outstandingWorkOrders.visibleItems.length > 5 && (
                        <IonButton mode="ios" fill="clear" onClick={outstandingWorkOrders.showLess}>
                          Show less
                        </IonButton>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PerformanceDashboard;
